"use client";
"use no memo";

// TanStack Table v8 mutates stable table instances; toolbar state must be read live.

import { Cross2Icon } from "@radix-ui/react-icons";
import { Table } from "@tanstack/react-table";

import {
  useImagingResultsColumnFilters,
  type ImagingResultsWithClassification,
} from "@/src/components/cards/imaging-results-card/imaging-results-columns";
import { Button } from "@/src/components/shadcn/ui/button";
import { DataTableFacetedFilter } from "@/src/components/tables/data-table-faceted-filter";
import { DataTableViewOptions } from "@/src/components/tables/data-table-view-options";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { getClassificationDisplay } from "@/src/lib/utils/classification";
import { Classification } from "@/src/models/constants/classifications";
import { ImagingType } from "@/src/models/constants/imaging-types";

interface ImagingResultsToolbarProps {
  table: Table<ImagingResultsWithClassification>;
}

export function ImagingResultsToolbar({ table }: ImagingResultsToolbarProps) {
  const isFiltered = (table.getState().columnFilters?.length ?? 0) > 0;
  const [_columnFilters, setColumnFilters] = useImagingResultsColumnFilters();

  // Get unique values for filters
  const rows = table.getFilteredRowModel()?.rows || [];
  const studyTypes = Array.from(
    new Set(rows.map((row) => row.original.studyType).filter((value): value is string => Boolean(value)))
  );
  const imagingTypes = Array.from(
    new Set(rows.map((row) => row.original.imagingType).filter((value): value is ImagingType => Boolean(value)))
  );
  const bodyParts = Array.from(
    new Set(rows.map((row) => row.original.bodyPart).filter((value): value is string => Boolean(value)))
  );
  const classifications = Array.from(new Set(rows.map((row) => row.original.classification).filter(Boolean)));

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center space-x-2 px-4 py-2 text-muted-foreground">
        {table.getColumn("classification") && (
          <DataTableFacetedFilter
            column={table.getColumn("classification")}
            title="Classification"
            options={classifications.map((classification) => ({
              label: getClassificationDisplay(classification as Classification),
              value: classification,
            }))}
            onChange={(values) => {
              setColumnFilters({ imagingResultsClassification: values });
            }}
          />
        )}
        {table.getColumn("studyType") && (
          <DataTableFacetedFilter
            column={table.getColumn("studyType")}
            title="Study Type"
            options={studyTypes.map((type) => ({
              label: type,
              value: type,
            }))}
            onChange={(values) => {
              setColumnFilters({ studyType: values });
            }}
          />
        )}
        {table.getColumn("imagingType") && (
          <DataTableFacetedFilter
            column={table.getColumn("imagingType")}
            title="Imaging Type"
            options={imagingTypes.map((type) => ({
              label: formatFromUnderscoreToHumanReadable(type),
              value: type,
            }))}
            onChange={(values) => {
              setColumnFilters({ imagingType: values });
            }}
          />
        )}
        {table.getColumn("bodyPart") && (
          <DataTableFacetedFilter
            column={table.getColumn("bodyPart")}
            title="Body Part"
            options={bodyParts.map((part) => ({
              label: part,
              value: part,
            }))}
            onChange={(values) => {
              setColumnFilters({ bodyPart: values });
            }}
          />
        )}
        {table.getColumn("needsSignature") && (
          <DataTableFacetedFilter
            column={table.getColumn("needsSignature")}
            title="Signature"
            options={[
              { label: "Yes", value: "true" },
              { label: "No", value: "false" },
            ]}
            onChange={(values) => {
              setColumnFilters({ needsSignature: values });
            }}
          />
        )}
        {isFiltered && (
          <Button
            variant="ghost"
            onClick={() => {
              setColumnFilters(null);

              table.resetColumnFilters();
            }}
            className="px-2 lg:px-3"
          >
            <span className="text-muted-foreground text-xs">Reset</span>
            <Cross2Icon className="ml-2 h-3 w-3 relative top-[0.9px]" />
          </Button>
        )}
      </div>
      <div className="flex items-center space-x-2 px-4 py-2">
        <DataTableViewOptions table={table} />
      </div>
    </div>
  );
}
